import {
  Box,
  ClickAwayListener,
  Grid,
  List,
  TextField,
  useTheme
} from '@mui/material'
import { ChangeEvent, ReactNode, useEffect, useState } from 'react'
import PopupSectionRow from 'components/popup/PopupSectionRow'
import { t } from 'i18next'
import { CheckmarkIcon } from 'assets/icons'
import Popup from 'components/popup/Popup'
import i18n from 'i18n'
import { Model } from 'types/model'
import { GroupDoc } from 'types/api/changes'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import Logger from 'services/Logger'

interface SelectItemWithSearchPopupProps {
  showSearch?: boolean
  searchHint?: string | undefined | null
  data: SelectableItem[] | undefined | null
  open: boolean
  setOpen?: (b: boolean) => void
  children: ReactNode
  selectedItemId: string | undefined // id field from the SelectableItem
  selectItem?: (item: SelectableItem) => void
  placement?: 'top' | 'right' | 'bottom' | 'left' | undefined
  rowBuilder?: (item: SelectableItem) => ReactNode
  title?: string
  width?: string
  onHide?: () => void
  emptyResultsMessage?: string
  containerId?: string
  scrollToKey?: string
  snapScroll?: boolean
  closeOnTouchOutside?: boolean
  doNotReactOnAway?: boolean
  maxHeight?: string
}

export interface SelectableItem {
  id?: string
  title: string
  subTitle?: string
  subTitleColor?: string
  subItems?: SelectableItem[]
  rightIcon?: ReactNode
  leftIcon?: ReactNode
  leftIconColor?: string
  notClickable?: boolean
  value?: any
  svgIconMaxWidth?: number
  selected?: boolean
  notClickableReason?: string
  photo?: string
  group?: Model<GroupDoc> | GroupDoc
  idPrefix?: string
  localId?: string
  inherited?: boolean
}

function Content(props: SelectItemWithSearchPopupProps) {
  const theme = useTheme()
  const [selectedItem, onSelectedLang] = useState<SelectableItem | undefined>(
    props.data?.find((e) => e.id === props.selectedItemId)
  )
  const [searchText, setSearchText] = useState<string>('')

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const onItemClick = (lang: SelectableItem) => {
    onSelectedLang(lang)
    if (props.selectItem) props.selectItem(lang)
  }

  const searchEnabled = props.showSearch

  useEffect(() => {
    // Scroll to the item with the id equal to scrollToKey
    Logger.blue('Search:ScrollToKey', props.scrollToKey, props.containerId)

    if (props.scrollToKey) {
      const element = document.getElementById(props.scrollToKey)

      if (element) {
        element.scrollIntoView({
          behavior: props.snapScroll ? 'instant' : 'smooth',
          block: 'start'
        })
      }
    }
  }, [props.scrollToKey])

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      touchEvent="onTouchStart"
      onClickAway={() => {
        if (
          props.setOpen &&
          LocalStorage.get(LocalStorageKey.SHOULD_KEEP_POPUP) !== '1' &&
          props.doNotReactOnAway !== true
        )
          props.setOpen(false)
      }}
    >
      <Box
        id={props.containerId}
        sx={{
          overflowY: 'auto',
          overflowX: 'hidden',
          maxHeight: props.maxHeight ?? '300px',
          maxWidth: props.width ?? undefined
        }}
      >
        <Grid
          container
          direction={'column'}
          sx={{ padding: theme.spacing(0, 4) }}
        >
          {props.title && (
            <div
              style={{
                color: theme.palette.text.primary,
                fontSize: '20px',
                marginBottom: 8
              }}
            >
              {props.title}
            </div>
          )}
          {searchEnabled ? (
            <TextField
              autoFocus
              placeholder={props.searchHint ?? ''}
              value={searchText}
              onChange={onSearchChange}
              variant="standard"
            />
          ) : (
            <></>
          )}
          {props.data?.length === 0 && props.emptyResultsMessage ? (
            <PopupSectionRow onlyTitle title={props.emptyResultsMessage} />
          ) : (
            <List sx={{ width: '100%' }}>
              {props.data
                ?.filter((item) => {
                  if (searchEnabled) {
                    return item.title
                      .toLowerCase()
                      .includes(searchText.toLowerCase())
                  }

                  return true
                })
                .map((item, index) => {
                  if (props.rowBuilder) {
                    return props.rowBuilder(item)
                  }

                  return (
                    <div style={{ width: '100%' }} key={index}>
                      <ItemRow
                        key={index}
                        {...props}
                        onItemClick={onItemClick}
                        selectedItem={selectedItem}
                        item={item}
                      />
                    </div>
                  )
                })}
            </List>
          )}
        </Grid>
      </Box>
    </ClickAwayListener>
  )
}

interface ItemRowProps extends SelectItemWithSearchPopupProps {
  onItemClick: (item: SelectableItem) => void
  selectedItem: SelectableItem | undefined
  item: SelectableItem
}

export function ItemRow(props: ItemRowProps) {
  const { item } = props
  const [showSubItems, onShowSubItems] = useState(false)

  return (
    <SelectItemWithSearchPopup
      data={item.subItems ?? []}
      searchHint={t('calendar')}
      selectedItemId={props.selectedItemId}
      open={showSubItems}
      placement={i18n.dir() === 'rtl' ? 'left' : undefined}
      setOpen={onShowSubItems}
      selectItem={props.selectItem}
    >
      <div>
        <PopupSectionRow
          actionId={`${item.idPrefix ?? ''}${item.id ?? ''}`}
          overflow={'hidden'}
          color={item.leftIconColor}
          icon={props.item.leftIcon}
          maxWidthIcon={props.item.svgIconMaxWidth}
          engulfEntireRow
          clickableTitle
          title={
            <div
              style={{
                width: 'auto'
              }}
            >
              {item.title}
            </div>
          }
          action={
            item.notClickable
              ? () => {}
              : () => {
                  if ((item.subItems?.length ?? 0) > 0) {
                    onShowSubItems(true)
                  } else {
                    props.onItemClick(item)
                    if (props.setOpen) props.setOpen(false)
                  }
                }
          }
          value={
            props.item.rightIcon != undefined ? (
              <Grid
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  if ((item.subItems?.length ?? 0) > 0) onShowSubItems(true)
                }}
              >
                {props.item.rightIcon}
              </Grid>
            ) : props.selectedItem?.id?.includes(item.id ?? '') ? (
              <CheckmarkIcon />
            ) : (
              <></>
            )
          }
        />
      </div>
    </SelectItemWithSearchPopup>
  )
}

export default function SelectItemWithSearchPopup(
  props: SelectItemWithSearchPopupProps
) {
  return (
    <Popup
      onHide={props.onHide}
      setOpen={props.setOpen}
      open={props.open}
      content={<Content {...props} />}
      width={props.width}
      closeOnTouchOutside={props.closeOnTouchOutside}
      placement={props.placement ?? (i18n.dir() === 'ltr' ? 'right' : 'left')}
      fallbackPlacements={
        i18n.dir() === 'ltr' ? ['right', 'left', 'top'] : ['left', 'top']
      }
    >
      {props.children}
    </Popup>
  )
}
